import React, { useEffect, useState } from 'react';
import MemberLayout from '../../components/MemberLayout';
import { getMyProfile, updateMyProfile, getMembershipPackages } from '../../services/api';

const PROVINCES = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo',
  'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape',
];

export default function MyMembership() {
  const [profile, setProfile] = useState(null);
  const [packages, setPackages] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const loadProfile = () => {
    getMyProfile().then((res) => setProfile(res.data)).catch(() => {});
  };

  useEffect(() => {
    loadProfile();
    getMembershipPackages().then((res) => setPackages(res.data)).catch(() => {});
  }, []);

  const startEditing = () => {
    setForm({
      fullName: profile.fullName || '',
      dateOfBirth: profile.dateOfBirth || '',
      gender: profile.gender || '',
      phoneNumber: profile.phoneNumber || '',
      residentialAddress: profile.residentialAddress || '',
      province: profile.province || '',
      occupation: profile.occupation || '',
      employer: profile.employer || '',
      membershipPackageId: '',
    });
    setEditing(true);
    setError('');
    setSuccess(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      payload.membershipPackageId = payload.membershipPackageId ? Number(payload.membershipPackageId) : null;
      const res = await updateMyProfile(payload);
      setProfile(res.data);
      setEditing(false);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save your changes.');
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <MemberLayout title="My Membership"><p>Loading...</p></MemberLayout>;

  return (
    <MemberLayout title="My Membership">
      <div className="card" style={{ maxWidth: 600 }}>
        {success && <div className="success-box">Your details have been updated.</div>}

        {!editing ? (
          <>
            <div className="grid grid-2">
              <div><div className="stat-label">Membership Number</div><p><strong>{profile.membershipNumber}</strong></p></div>
              <div><div className="stat-label">Status</div><p><span className="badge badge-pending">{profile.status}</span></p></div>
              <div><div className="stat-label">Package</div><p>{profile.packageName || '—'} {profile.packagePrice ? `(${profile.packagePrice}/month)` : ''}</p></div>
              <div><div className="stat-label">Joining Date</div><p>{profile.joiningDate}</p></div>
              <div><div className="stat-label">Renewal / Expiry Date</div><p>{profile.expiryDate || '—'}</p></div>
              <div><div className="stat-label">Full Name</div><p>{profile.fullName}</p></div>
              <div><div className="stat-label">Phone Number</div><p>{profile.phoneNumber}</p></div>
              <div><div className="stat-label">Province</div><p>{profile.province}</p></div>
              <div style={{ gridColumn: '1 / -1' }}><div className="stat-label">Residential Address</div><p>{profile.residentialAddress || '—'}</p></div>
              <div><div className="stat-label">Occupation</div><p>{profile.occupation || '—'}</p></div>
              <div><div className="stat-label">Employer</div><p>{profile.employer || '—'}</p></div>
            </div>

            <div style={{ marginTop: 20, padding: 14, background: '#f7f5ef', borderRadius: 4, fontSize: '0.85rem' }}>
              {profile.editAllowed ? (
                <>You've been given permission to update your details. This can be used once — after you save, you'll need to request access again for further changes.</>
              ) : (
                <>Your details are locked. Contact the organisation (WhatsApp or the Contact page) if something needs to be corrected, and we'll grant you access to update it.</>
              )}
            </div>

            {profile.editAllowed && (
              <button className="btn btn-gold" style={{ marginTop: 16 }} onClick={startEditing}>
                Edit My Details
              </button>
            )}
          </>
        ) : (
          <form onSubmit={handleSave}>
            {error && <p className="error-text" style={{ marginBottom: 16 }}>{error}</p>}
            <div className="form-row">
              <div className="form-group">
                <label>Full Name</label>
                <input required value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input required value={form.phoneNumber} onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Date of Birth</label>
                <input type="date" value={form.dateOfBirth} onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Gender</label>
                <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Residential Address</label>
              <input value={form.residentialAddress} onChange={(e) => setForm({ ...form, residentialAddress: e.target.value })} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Province</label>
                <select value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })}>
                  <option value="">Select your province</option>
                  {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Occupation</label>
                <input value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label>Employer</label>
              <input value={form.employer} onChange={(e) => setForm({ ...form, employer: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Membership Package</label>
              <select
                value={form.membershipPackageId}
                onChange={(e) => setForm({ ...form, membershipPackageId: e.target.value })}
              >
                <option value="">Keep current package ({profile.packageName || 'none selected'})</option>
                {packages.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} (R{p.monthlyFee})</option>
                ))}
              </select>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-grey)', marginTop: 6 }}>
                Changing your package takes effect immediately and applies from your next renewal.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-gold" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
              <button type="button" className="btn btn-outline-dark" onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </form>
        )}
      </div>
    </MemberLayout>
  );
}
